import { ProjectStyle, type Project } from '@jira/platform__project-types/src/project-types.tsx';

type ProjectType =
	| 'jira_software'
	| 'jira_service_management'
	| 'jira_work_management'
	| 'jira_product_discovery'
	| 'jira_customer_service';

type AvatarsSizeMap = {
	['16x16']: string;
	['24x24']: string;
	['32x32']: string;
	['48x48']: string;
};

type ProjectTypeKey = 'software' | 'product_discovery' | 'business' | 'service_desk' | string;

type ProjectBean = {
	id: string;
	key: string;
	name: string;
	projectTypeKey: ProjectTypeKey;
	simplified: boolean;
	avatarUrls: AvatarsSizeMap;
	/**
	 * Determine is a project has been set as a custom template
	 */
	isLiveTemplate?: boolean;
};

type ProjectPermission =
	| 'ADD_COMMENTS'
	| 'ADMINISTER_PROJECTS'
	| 'CREATE_ATTACHMENTS'
	| 'CREATE_ISSUES'
	| 'DELETE_ALL_ATTACHMENTS'
	| 'DELETE_ALL_COMMENTS'
	| 'DELETE_OWN_ATTACHMENTS'
	| 'EDIT_ISSUE_LAYOUT'
	| 'EDIT_ISSUES'
	| 'EDIT_WORKFLOW'
	| 'LINK_ISSUES'
	| 'MANAGE_VERSIONS';

type PermissionInfo = {
	isAdmin: boolean;
	isSiteAdmin: boolean;
	projectPermissionsQueried: Record<ProjectPermission, boolean>;
};

type BoardBean = {
	id: number;
	name: string;
	type?: 'simple' | 'kanban' | 'scrum';
};

type FlexibleNomenclatureData = {
	levelOneName: string;
};

type JiraSoftwareNavData = {
	boardInfo?: BoardBean;
	flexibleNomenclatureData?: FlexibleNomenclatureData;
	hasBacklog: boolean;
	hasBoard: boolean;
	hasCalendar: boolean;
	hasCode: boolean;
	hasComponents: boolean;
	hasDeployments: boolean;
	hasDevelopment: boolean;
	hasForms: boolean;
	hasGoals: boolean;
	hasGetStarted: boolean;
	hasIncidents: boolean;
	hasIssues: boolean;
	hasList: boolean;
	hasOpsgenieSchedule: boolean;
	hasPages: boolean;
	hasReleases: boolean;
	hasReports: boolean;
	hasRequests: boolean;
	hasRoadmap: boolean;
	hasSecurity: boolean;
	hasSprint: boolean;
	hasSummaryPage: boolean;
	/**
	 * Whether the project has explicit field associations (EFA) enabled.
	 * This is a temporary field and will be removed in the future when EFA is enabled for all tenants.
	 */
	hasExplicitFieldAssociationsEnabled?: boolean;
	permissionInfo: PermissionInfo;
	project: ProjectBean;
	hasArchivedIssues?: boolean;
};

type JiraServiceManagementNavData = {
	project: ProjectBean;
	permissionInfo: PermissionInfo;
	isAgent: boolean;
	isAdminAgent: boolean;
	hasCustomerChannels: boolean;
	hasNotificationLogs: boolean;
	hasHelpCenterSettings: boolean;
	hasArchivedIssues?: boolean;
	/**
	 * Whether the project has explicit field associations (EFA) enabled.
	 * This is a temporary field and will be removed in the future when EFA is enabled for all tenants.
	 */
	hasExplicitFieldAssociationsEnabled?: boolean;
	knowledgeBaseInfo: {
		enabled: boolean;
		knowledgeBaseType: 'hub-nested' | 'hub' | 'base' | null;
		isKnowledgeBaseLinked: boolean;
	};
	raiseRequestInfo: {
		enabled: boolean;
		portalId: number;
		portalUrl: string;
	};
	opsGenieInfo: {
		isOnCallEnabled: boolean;
		isAlertEnabled: boolean;
		opsGenieBaseUrl: string;
	};
	settingsInfo: {
		hasChangeManagement: boolean;
		hasITSMFeatures: boolean;
		hasAddonInstaller: boolean;
		hasWidgetAddon: boolean;
		hasChat: boolean;
		hasIncidentManagement: boolean;
		hasCaseManagement?: boolean;
		hasExternalLinks: boolean;
		hasProjectAutomation: boolean;
		hasServices: boolean;
		hasVersions: boolean;
		hasComponents: boolean;
		hasCustomerSupportManagement: boolean;
		hasDeveloperEscalation: boolean;
		hasProductsAndEntitlements: boolean;
		hasVirtualAgent: boolean;
		hasLanguageSupport: boolean;
		hasRequestTypes: boolean;
		hasPortalSettings: boolean;
		hasCustomerNotifications: boolean;
		hasCustomerPermissions: boolean;
		hasForms: boolean;
		hasKnowledgeBase: boolean;
		hasChannelsAndSelfService: boolean;
	};
	projectSummaryInfo?: {
		enabled: boolean;
	};
	boardInfo: {
		enabled: boolean;
		// If board does not exist empty object will be returned
		board: Partial<BoardBean>;
	};
	calendarInfo: {
		enabled: boolean;
	};
};

type JiraWorkManagementNavData = {
	project: ProjectBean;
	permissionInfo: PermissionInfo;
	hasComponents: boolean;
	/**
	 * Whether the project has explicit field associations (EFA) enabled.
	 * This is a temporary field and will be removed in the future when EFA is enabled for all tenants.
	 */
	hasExplicitFieldAssociationsEnabled?: boolean;
};

type JiraProductDiscoveryNavData = {
	project: ProjectBean;
	permissionInfo: PermissionInfo;
};

type ProjectSidebarNavInfo = {
	projectType: ProjectType;
	jiraSoftwareNavData?: JiraSoftwareNavData;
	jiraServiceManagementNavData?: JiraServiceManagementNavData;
	jiraWorkManagementNavData?: JiraWorkManagementNavData;
	jiraProductDiscoveryNavData?: JiraProductDiscoveryNavData;
};

export const getStyle = (isSimplifiedProject: boolean): ProjectStyle =>
	isSimplifiedProject ? ProjectStyle.NEXT_GEN : ProjectStyle.CLASSIC;

const getProject = (project: ProjectBean): Project => ({
	avatarUrl: project.avatarUrls['48x48'],
	id: Number(project.id),
	isSimplified: project.simplified,
	key: project.key,
	name: project.name,
	style: getStyle(project.simplified),
	type: project.projectTypeKey,
	isLiveTemplate: project.isLiveTemplate ?? false,
});

export const extractData = (data: ProjectSidebarNavInfo): Project | null => {
	const {
		projectType,
		jiraWorkManagementNavData,
		jiraSoftwareNavData,
		jiraServiceManagementNavData,
		jiraProductDiscoveryNavData,
	} = data || {};

	switch (projectType) {
		case 'jira_work_management':
			return jiraWorkManagementNavData?.project
				? getProject(jiraWorkManagementNavData.project)
				: null;
		case 'jira_software':
			return jiraSoftwareNavData?.project ? getProject(jiraSoftwareNavData.project) : null;
		case 'jira_service_management':
		case 'jira_customer_service':
			return jiraServiceManagementNavData?.project
				? getProject(jiraServiceManagementNavData.project)
				: null;
		case 'jira_product_discovery':
			return jiraProductDiscoveryNavData?.project
				? getProject(jiraProductDiscoveryNavData.project)
				: null;
		case null:
		default:
			return null;
	}
};
